import { useEffect, useState } from 'react';

import { CACHE_MS } from '../../constants/index.js';

import type {
  UseMyDatasetRoleParams,
  UseMyDatasetRoleResult,
} from './useMyDatasetRole.interface.js';

interface CacheEntry {
  roles: string[];
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();

export function resetDatasetRoleCache(): void {
  cache.clear();
}

export function useMyDatasetRole({
  dataSetId,
  service,
}: UseMyDatasetRoleParams): UseMyDatasetRoleResult {
  const [roles, setRoles] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const cached = cache.get(dataSetId);
    if (cached && Date.now() - cached.fetchedAt < CACHE_MS) {
      setRoles(cached.roles);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setRoles(null);
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const response = await service.getMyDatasetRole(dataSetId);
        if (!cancelled) {
          cache.set(dataSetId, {
            roles: response.roles,
            fetchedAt: Date.now(),
          });
          setRoles(response.roles);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          cache.delete(dataSetId);
          setRoles([]);
          setError(
            err instanceof Error ? err : new Error('Failed to load role'),
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dataSetId, service]);

  return { roles, isLoading, error };
}
